import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { queryContract, getOpenInterest } from "../functions/Contract";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPrice } from "../../ReduxSlices/TradeSlice";
import { getCoinId, isCityMarket, getMarketByTokenIndex, getMarketById, MARKET_CATEGORIES, getCryptoMarkets, getRealEstateMarkets, getRWAMarkets, getWatchMarkets, getStockMarkets, getCommodityMarkets, getPocMarkets } from "../../config/markets";
import { rateLimitedFetch } from "../../utils/coinGeckoRateLimiter";
import { resolveMarketIcon } from "../../utils/marketIcons";
import { resolveMaxLeverage } from "../../utils/leverageUtils";
import { getNibiruSDK, getContractAddresses } from "../../config/network";
import { formatPriceWithSubscript, formatWithSubscript } from "../utils/formatter";
import PriceUpdateBadge from "./PriceUpdateBadge";
import { NumberTicker } from "./NumberAnimation";
import MarketSwitcher from "../Cockpit/MarketSwitcher";
import MarketSelector from "../MarketSelect/MarketSelector";
import { MarketObject } from "../MarketSelect/SelectorObjects3D";
import { describeMarket, THEMES } from "../MarketSelect/selectorMarkets";
// Collectibles are not in the selector's shape map — they have their own slab
// and pack objects in the cockpit.
import { MarketObjectFace } from "../Cockpit/Objects3D";
import { getCockpitMarket } from "../Cockpit/cockpitMarkets";
import { SELECTOR_CATEGORIES } from "../MarketSelect/selectorMarkets";

// Topbar SVG icons
import highlightedStarIcon from "../../assets/images/Perps/topbar/highlightedstar.svg";
import unhighlightedStarIcon from "../../assets/images/Perps/topbar/unhighlightedstar.svg";
import upArrowIcon from "../../assets/images/Perps/topbar/up.svg";
import downArrowIcon from "../../assets/images/Perps/topbar/down.svg";

// Watch brand logo imports
import watchIndexImg from "../../assets/images/Perps/watch/watch-index.png";
import rolexImg from "../../assets/images/Perps/watch/rolex.png";
import patekImg from "../../assets/images/Perps/watch/patek.png";
import apImg from "../../assets/images/Perps/watch/ap.png";
import omegaImg from "../../assets/images/Perps/watch/omega.png";
import cartierImg from "../../assets/images/Perps/watch/cartier.png";
import breitlingImg from "../../assets/images/Perps/watch/breitling.png";
import tudorImg from "../../assets/images/Perps/watch/tudor.png";

const watchIconMap = {
  '/assets/images/Perps/watch/watch-index.png': watchIndexImg,
  '/assets/images/Perps/watch/rolex.png': rolexImg,
  '/assets/images/Perps/watch/patek.png': patekImg,
  '/assets/images/Perps/watch/ap.png': apImg,
  '/assets/images/Perps/watch/omega.png': omegaImg,
  '/assets/images/Perps/watch/cartier.png': cartierImg,
  '/assets/images/Perps/watch/breitling.png': breitlingImg,
  '/assets/images/Perps/watch/tudor.png': tudorImg,
};

const nibiru = getNibiruSDK();

/**
 * The market button's rect, for the selector to grow out of and fold back
 * into. Read from the DOM rather than a ref: the button only exists in the
 * desktop row, while the same open handler is wired to the mobile control and
 * the keyboard shortcut. Nothing to anchor to means the overlay just fades.
 */
const readMarketButtonRect = () => {
  const el = document.querySelector('.ce-market-btn');
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 4 ? r : null;
};

// How long an all-market price sweep stays fresh. The browsers (3D selector,
// its flat list, collectibles switcher) all read the same snapshot, so this
// stops a fetch storm when a trader moves between them.
const ALL_PRICES_TTL_MS = 60 * 1000;

/**
 * Per-category accent, mirroring `THEMES` in the market selector so the trading
 * page and the browser you reached it from agree. Keyed by
 * `MARKET_CATEGORIES` values rather than the selector's tab keys.
 *
 * `rwa` (the Pokédex markets) is deliberately absent: the cockpit is themed by
 * the loaded ball, not by this, and falls through to the default.
 */
/**
 * Categories whose product is an index rather than the asset itself. Only these
 * get "INDEX" appended to the eyebrow — "CRYPTO INDEX" over a BTC chart would
 * be wrong, you are trading BTC.
 */
const INDEX_CATEGORIES = new Set([
  MARKET_CATEGORIES.REAL_ESTATE,
  MARKET_CATEGORIES.WATCHES,
  MARKET_CATEGORIES.RWA,
]);

/**
 * The market identity block — the selector's 3D object, the category eyebrow,
 * the name and the pair, as one clickable control that opens the browser.
 *
 * The object is the same component the coverflow uses, so the market a trader
 * span into is the market they land on. It is rendered at full size and scaled
 * down with a transform rather than re-authored small: the shapes are built
 * from stacked 3D faces whose offsets are in pixels, and rebuilding them at
 * header size would mean a second set of geometry to keep in sync.
 *
 * `.ce-raw` matters — the dark-theme sweep skips those subtrees, and these are
 * lit materials, not surfaces. Without it gold reads as grey (see theme.css).
 */
const MarketIdentity = ({ market, maxLeverage, onOpen, compact = false }) => {
  const described = useMemo(() => (market ? describeMarket(market) : null), [market]);
  // PSA-10 and POKE are graded cards, not coins. `describeMarket` has no shape
  // for them and falls through to a coin, so they come from the cockpit table.
  const cockpit = useMemo(() => (market ? getCockpitMarket(market.id) : null), [market]);
  // Same resolution the selector passes as `getLogo`. Without it the coin face
  // and the bar's stamp render blank — the shapes draw the logo, they do not
  // fall back to the ticker.
  const logo = useMemo(() => {
    if (!market) return null;
    if (market.iconPath) return getImageUrl(market.iconPath);
    return market.coinGeckoId ? getCoinGeckoImageUrl(market.coinGeckoId, market.id) : null;
  }, [market]);
  if (!market) return null;

  // THEMES has no `rwa` entry — the selector excludes collectibles — so it
  // would otherwise print the raw key, "RWA".
  const catLabel = (
    THEMES[market.category]?.cat ||
    (market.category === MARKET_CATEGORIES.RWA ? "Collectibles" : market.category) ||
    ""
  ).toUpperCase();
  const eyebrow = INDEX_CATEGORIES.has(market.category) ? `${catLabel} INDEX` : catLabel;
  // `short` is the selector's compact code (LA, NYC, XAU) — "LOSANGELES/USDC"
  // twice over is unreadable at this size, and the object already carries the
  // long name.
  const pair = `${described?.short || market.symbol}/USDC`;
  const scale = compact ? 0.23 : 0.34;
  const box = compact ? 56 : 84;

  return (
    <div
      role="button"
      aria-label="Change market"
      onClick={onOpen}
      className="ce-market-btn flex items-center cursor-pointer"
      style={{
        gap: compact ? 10 : 14,
        padding: compact ? "6px 10px" : "10px 16px",
        borderRadius: 14,
        // A floor, not a fixed width. The block may shrink so the row never
        // overflows, but not past the point where the market name is a single
        // truncated letter — below this the name is no longer identification.
        minWidth: compact ? 214 : 300,
        flex: "0 1 auto",
        overflow: "hidden",
      }}
    >
      {/* Fixed box, object scaled inside it — the object's own footprint is
          186-232px and would otherwise blow the header open. */}
      <div
        className="ce-raw"
        aria-hidden="true"
        style={{
          width: box, height: box, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "visible", pointerEvents: "none",
        }}
      >
        {cockpit ? (
          // Slab/pack size directly rather than scaled — these take a `size`
          // prop, so there is no fixed geometry to shrink around.
          <MarketObjectFace m={cockpit} w={box} />
        ) : (
          <div style={{ transform: `scale(${scale})`, transformOrigin: "center" }}>
            {described && <MarketObject m={described} logo={logo} />}
          </div>
        )}
      </div>

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: "General Sans", fontSize: compact ? 9.5 : 10.5, fontWeight: 700,
            letterSpacing: ".16em", textTransform: "uppercase",
            color: "var(--cat-accent, #5B1DEE)", whiteSpace: "nowrap",
          }}
        >
          {eyebrow}
        </div>
        <div className="flex items-baseline" style={{ gap: 8, minWidth: 0 }}>
          <span
            className="truncate"
            style={{
              fontFamily: "General Sans", fontSize: compact ? 17 : 22, fontWeight: 700,
              letterSpacing: "-.02em", color: "var(--ink)", lineHeight: 1.15,
            }}
          >
            {market.name}
          </span>
          <span
            style={{
              fontFamily: "General Sans", fontSize: compact ? 11 : 13, fontWeight: 600,
              color: "var(--ink-3)", whiteSpace: "nowrap",
            }}
          >
            {pair}
          </span>
        </div>
        {/* The pair is already printed beside the name, so in the compact
            skin this line carries only what is not said twice. */}
        <div
          style={{
            fontFamily: "General Sans", fontSize: compact ? 10.5 : 12, fontWeight: 500,
            color: "var(--ink-2)", whiteSpace: "nowrap", marginTop: 1,
            // Truncates with the name above it. Left at its natural width this
            // line set the block's min-content size, so the block could not
            // give up any width no matter what the row needed.
            overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          {compact ? `up to ${maxLeverage}× leverage` : `${pair} · up to ${maxLeverage}× leverage`}
        </div>
      </div>

      {/* The whole block has always opened the market browser, but nothing said
          so — it reads as a heading, and headings are not clickable. The pill
          is the affordance; it fires the same `onOpen`, so the large target
          stays for anyone who has already learned it. */}
      <button
        type="button"
        className="ce-market-swap"
        onClick={(e) => { e.stopPropagation(); onOpen?.(); }}
        title="Change market"
        aria-label="Change market"
        style={{ padding: compact ? "6px 10px" : "7px 12px", fontSize: compact ? 11.5 : 12.5 }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
          <path d="M2.5 5.5h9.5M9.5 3l2.5 2.5L9.5 8M13.5 10.5H4M6.5 8L4 10.5 6.5 13"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {!compact && <span>Change</span>}
      </button>
    </div>
  );
};

/**
 * The categories that carry an accent of their own.
 *
 * The colours themselves live in `neumorph2026.css` §0, keyed on the same
 * slugs, because each one needs a dark-mode lift and a set of derived values
 * (`-lift`, `-soft`, `-glow`) that only CSS can swap per theme. This set is
 * the membership test: anything not in it falls back to the brand purple.
 */
const CATEGORY_ACCENT = {
  [MARKET_CATEGORIES.CRYPTO]: true,
  [MARKET_CATEGORIES.STOCKS]: true,
  [MARKET_CATEGORIES.COMMODITIES]: true,
  [MARKET_CATEGORIES.REAL_ESTATE]: true,
  [MARKET_CATEGORIES.WATCHES]: true,
};

// Module-level cache for real estate prices
const realEstatePriceCache = {
  data: {},
  timestamp: 0,
  fetching: false,
};

const REAL_ESTATE_CACHE_TTL = 5 * 60 * 1000;

const fetchSingleCityPrice = async (api, cityId) => {
  try {
    const response = await api('parcl/city-data', { cityId });
    if (response?.success && response.data) {
      const { PriceData } = response.data;
      const sorted = [...PriceData].sort((a, b) => new Date(b.date) - new Date(a.date));
      const todayPrice = sorted[0]?.price || 0;
      const yesterdayPrice = sorted[1]?.price || todayPrice;
      const change24h = yesterdayPrice > 0
        ? ((todayPrice - yesterdayPrice) / yesterdayPrice) * 100
        : 0;
      return { price: todayPrice, change24h };
    }
  } catch (err) {
    console.warn(`Failed to fetch price for ${cityId}:`, err.message);
  }
  return null;
};

const prefetchAllRealEstatePrices = async (realEstateMarketsList) => {
  const now = Date.now();
  if (Object.keys(realEstatePriceCache.data).length > 0 && now - realEstatePriceCache.timestamp < REAL_ESTATE_CACHE_TTL) {
    return realEstatePriceCache.data;
  }
  if (realEstatePriceCache.fetching) return realEstatePriceCache.data;
  realEstatePriceCache.fetching = true;

  try {
    const module = await import("../functions/Apiwithouttoken");
    const api = module.api || module.default?.api;
    if (!api) { realEstatePriceCache.fetching = false; return {}; }

    const results = await Promise.all(
      realEstateMarketsList.map(async (market) => {
        const data = await fetchSingleCityPrice(api, market.id);
        return { id: market.id, data };
      })
    );

    const prices = {};
    results.forEach(({ id, data }) => {
      if (data) prices[id] = data;
    });

    realEstatePriceCache.data = prices;
    realEstatePriceCache.timestamp = Date.now();
    return prices;
  } catch (err) {
    console.error('Failed to prefetch real estate prices:', err);
    return realEstatePriceCache.data;
  } finally {
    realEstatePriceCache.fetching = false;
  }
};

const lowPriceTokenIds = new Set(["pump", "pengu", "bonk", "shib", "nibi"]);

// formatPriceWithSubscript imported from utils/formatter

function getImageUrl(iconPath) {
  if (!iconPath) return null;

  // Check watch icon map first (explicit imports are most reliable)
  if (watchIconMap[iconPath]) {
    return watchIconMap[iconPath];
  }

  return resolveMarketIcon(iconPath);
}

function getCoinGeckoImageUrl(coinGeckoId, marketId) {
  if (!coinGeckoId) return null;

  const imageMap = {
    'bitcoin': { id: '1', filename: 'bitcoin.png' },
    'ethereum': { id: '279', filename: 'ethereum.png' },
    'cosmos': { id: '1481', filename: 'cosmos_hub.png' },
    'solana': { id: '4128', filename: 'solana.png' },
    'ripple': { id: '44', filename: 'xrp-symbol-white-128.png' },
    'litecoin': { id: '2', filename: 'litecoin.png' },
    'binancecoin': { id: '825', filename: 'bnb-icon2_2x.png' },
    'near': { id: '10365', filename: 'near.jpg' },
    'hyperliquid': { id: '50882', filename: 'hyperliquid.jpg' },
    'official-trump': { id: '53746', filename: 'trump.png' },
    'sui': { id: '26375', filename: 'sui-ocean-square.png'},
    'ethena': { id: '36530', filename: 'ethena.png'},
    'arbitrum': { id: '16547', filename: 'arb.jpg'},
    'tron': { id: '1094', filename: 'tron-logo.png'},
    'aptos': { id: '26455', filename: 'Aptos-Network-Symbol-Black-RGB-1x.png'},
    'polygon-ecosystem-token': { id: '32440', filename: 'pol.png'},
    'polygon': { id: '32440', filename: 'pol.png'},
    'the-open-network': { id: '17980', filename: 'photo_2024-09-10_17.09.00.jpeg'},
    'cardano': { id: '975', filename: 'cardano.png'},
    'dogecoin': { id: '5', filename: 'dogecoin.png'},
    'doge': { id: '51625', filename: '_doge.jpg'},
    'aster-2': { id: '69040', filename: '_ASTER.png'},
    'aster': { id: '69040', filename: '_ASTER.png'},
    'pump-fun': { id: '67164', filename: 'pump.jpg'},
    'pump': { id: '67164', filename: 'pump.jpg'},
    'avalanche-2': { id: '12559', filename: 'Avalanche_Circle_RedWhite_Trans.png'},
    'avax': { id: '12559', filename: 'Avalanche_Circle_RedWhite_Trans.png'},
    'aave': { id: '12645', filename: 'aave-token-round.png'},
    'pudgy-penguins': { id: '52622', filename: 'PUDGY_PENGUINS_PENGU_PFP.png'},
    'chainlink': { id: '877', filename: 'Chainlink_Logo_500.png'},
    'bonk': { id: '28600', filename: 'bonk.jpg'},
    'shiba-inu': { id: '11939', filename: 'shiba.png'},
    'shib': { id: '11939', filename: 'shiba.png'},
    'kaito': { id: '54411', filename: 'Qm4DW488_400x400.jpg'},
    'mantle': { id: '30980', filename: 'Mantle-Logo-mark.png'},
    'mnt': { id: '30980', filename: 'Mantle-Logo-mark.png'},
    'story-2': { id: '54035', filename: 'Transparent_bg.png'},
    'ip': { id: '54035', filename: 'Transparent_bg.png'},
    'virtual-protocol': { id: '34057', filename: 'LOGOMARK.png'},
    'zcash': { id: '486', filename: 'circle-zcash-color.png'},
    'nibiru': {id : '34980', filename: 'favicon-256x256.png'}
  };

  const imageInfo = imageMap[coinGeckoId];
  if (!imageInfo) return null;

  return `https://coin-images.coingecko.com/coins/images/${imageInfo.id}/large/${imageInfo.filename}`;
}

const formatCurrencyWithSuffix = (value) => {
  if (!value || value === "$0M" || value === "$0") return value;

  const match = value.match(/\$([0-9,.]+)([KMB]?)/);
  if (!match) return value;

  const numberPart = parseFloat(match[1].replace(/,/g, ''));
  const suffix = match[2] || '';

  const formattedNumber = numberPart.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `$${formattedNumber}${suffix}`;
};

// Favorites localStorage helpers
const FAVORITES_KEY = 'coded_estate_favorite_markets';
const getFavorites = () => {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
};
const toggleFavorite = (marketId) => {
  const favs = getFavorites();
  const idx = favs.indexOf(marketId);
  if (idx >= 0) {
    favs.splice(idx, 1);
  } else {
    favs.push(marketId);
  }
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
  return favs;
};

// Format OI values
const formatOIValue = (value) => {
  if (!value) return "$0";
  const num = parseFloat(value);
  if (isNaN(num)) return "$0";
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
};

export const TradingTopBar = ({ tokenIndex, marketName = "Next Price Update:" }) => {
  const navigate = useNavigate();
  const price = useSelector((state) => state.trade.currentPrice);
  useSelector((state) => state.liquidation);
  const [marketData, setMarketData] = useState({
    price: 0,
    change24h: 0,
    volume24h: "$0M",
  });
  const [isRealEstate, setIsRealEstate] = useState(false);
  const [assetInfoOpen, setAssetInfoOpen] = useState(false);
  const [marketInfo, setMarketInfo] = useState(null);

  // Stocks and commodities price off the candles API rather than the oracle.
  // Their market-hours countdown lives in `PriceUpdateBadge` now, with the
  // oracle ones.
  const currentMarket = getMarketByTokenIndex(tokenIndex);
  const isStockMarket = currentMarket?.category === MARKET_CATEGORIES.STOCKS;
  const isCommodityMarket = currentMarket?.category === MARKET_CATEGORIES.COMMODITIES;

  /**
   * Publish the market's category as `data-cat` on <html>.
   *
   * The market selector already themes each category — stocks green,
   * commodities gold, real estate terracotta, watches blue, crypto purple. The
   * trading page was purple for every market, so browsing a gold commodities
   * bar and then landing on a purple trading page read as two products.
   *
   * This used to write six `--cat-accent-*` values inline. It publishes an
   * attribute instead, and `neumorph2026.css` §0 derives the whole set from
   * it, because the 2026 restyle needs a *dark-mode lift* per accent: #5B1DEE
   * is a fine purple on #FAFAFA and a bruise on #0D0F14. An inline style on
   * <html> outranks every stylesheet rule, so a JS-published accent could
   * never be lifted by a `[data-theme="dark"]` block — the attribute can.
   *
   * It goes on <html> rather than a page wrapper because the accent has to
   * reach portalled overlays (modals, tooltips) as well as the page, and every
   * trading page mounts this top bar. Cleared on unmount so non-trading routes
   * fall back to the token's own default.
   */
  useEffect(() => {
    const category = currentMarket?.category;
    const root = document.documentElement;
    // Only the five categories the sheet knows about. `rwa` and `forex` have
    // no accent of their own and fall through to the brand purple default.
    root.setAttribute("data-cat", CATEGORY_ACCENT[category] ? category : "default");
    // Marks the page as having a market accent. The page-level corner glow is
    // gated on this — off a trading route there is no accent, and an ungated
    // rule would wash every page in brand purple.
    root.classList.add("ce-market-page");
    return () => {
      root.removeAttribute("data-cat");
      root.classList.remove("ce-market-page");
    };
  }, [currentMarket?.category]);

  // Collectibles is presented as a full-screen 3D coverflow rather than a list;
  // every other category browses as the full-screen 3D selector. Those two are
  // the only market browsers — the flat dropdown list that used to layer over
  // them is gone, and with it the search, sort, favourites and keyboard-nav
  // state it owned. Both selectors search and filter their own contents.
  const [showCollectiblesSwitcher, setShowCollectiblesSwitcher] = useState(false);
  const [showMarketSelector, setShowMarketSelector] = useState(false);
  const [selectorCategory, setSelectorCategory] = useState(null);
  // Where the selector opens out of and collapses back into.
  const [selectorAnchor, setSelectorAnchor] = useState(null);
  // True while the browser on screen was handed over from the other one rather
  // than opened from the trading page. Both are full-screen, so that swap is a
  // crossfade in place; only the trading page is somewhere to travel to.
  const [browserHandoff, setBrowserHandoff] = useState(false);
  const [allMarketPrices, setAllMarketPrices] = useState({});
  // When the all-market price sweep last completed, so opening the selector,
  // its list and the collectibles switcher in quick succession reuses one fetch.
  const lastPriceFetchRef = useRef(0);
  const [favorites, setFavorites] = useState(getFavorites());
  const dropdownRef = useRef(null);

  // OI & funding state
  const [oiData, setOiData] = useState({ longPct: 0, shortPct: 0, longVal: 0, shortVal: 0, maxOI: 0 });
  const [fundingRate, setFundingRate] = useState(null);
  const [fundingRateRaw, setFundingRateRaw] = useState(null); // raw pct value for subscript display
  // Max leverage as reported by the keeper from the on-chain market config. The
  // static markets.js value is only a fallback until this arrives — the header
  // must not advertise 100x on a market the contract caps at 50x.
  const [contractMaxLeverage, setContractMaxLeverage] = useState(null);

  // Tooltip state
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
  const tooltipTimeoutRef = useRef(null);

  // Track previous price to detect changes
  const previousPriceRef = useRef(price);

  const dispatch = useDispatch();

  // Get markets for dropdown
  const cryptoMarkets = getCryptoMarkets();
  const realEstateMarkets = getRealEstateMarkets();
  const rwaMarkets = getRWAMarkets();
  const watchMarkets = getWatchMarkets();
  const stockMarkets = getStockMarkets();
  const commodityMarkets = getCommodityMarkets();

  // Map market category to dropdown tab key
  const categoryToTab = (cat) => {
    const map = {
      [MARKET_CATEGORIES.CRYPTO]: 'crypto',
      [MARKET_CATEGORIES.STOCKS]: 'stocks',
      [MARKET_CATEGORIES.COMMODITIES]: 'commodities',
      [MARKET_CATEGORIES.REAL_ESTATE]: 'realestate',
      [MARKET_CATEGORIES.RWA]: 'rwa',
      [MARKET_CATEGORIES.WATCHES]: 'watches',
    };
    return map[cat] || 'all';
  };

  /**
   * The trading page's only way into market selection.
   *
   * The top bar used to drop this list down under the ticker, with a "Browse in
   * 3D" button tucked in its header. That made the 3D coverflow the secondary
   * path when it is the better one for browsing, so the entry point is flipped:
   * the button opens the 3D selector, and the list is reachable from inside it
   * for traders who already know the ticker they want.
   */
  const openMarketBrowser = useCallback(() => {
    // The market button is a toggle, not just an opener: pressing it again
    // while a browser is open closes it, the way the control that opened a
    // dropdown always does.
    if (showMarketSelector || showCollectiblesSwitcher) {
      setShowMarketSelector(false);
      setShowCollectiblesSwitcher(false);
      return;
    }
    const mkt = getMarketByTokenIndex(tokenIndex);
    const tab = mkt ? categoryToTab(mkt.category) : 'all';
    // A collectibles market is being traded inside the Pokédex, so it browses
    // with the device's own switcher rather than the app-chrome selector —
    // dropping the trader into light app chrome from inside the device reads as
    // a different product. Both browsers now carry the same list button, so the
    // fast path is identical either way.
    if (tab === 'rwa') {
      setSelectorAnchor(readMarketButtonRect());
      setBrowserHandoff(false);
      setShowCollectiblesSwitcher(true);
      return;
    }
    // The selector has no "all"/"favorites" plate, so those two open on the
    // trader's own category instead.
    setSelectorCategory(tab === 'all' || tab === 'favorites' ? null : tab);
    setSelectorAnchor(readMarketButtonRect());
    setBrowserHandoff(false);
    setShowMarketSelector(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenIndex, showMarketSelector, showCollectiblesSwitcher]);

  // The click-outside, Escape and Ctrl+/ handlers that lived here belonged to
  // the flat dropdown list. Both 3D browsers dismiss themselves — backdrop,
  // Escape and the market button that opened them — so there is nothing left
  // for a document-level handler to close.

  // Pre-fetch real estate prices on mount
  useEffect(() => {
    if (realEstateMarkets.length === 0) return;
    prefetchAllRealEstatePrices(realEstateMarkets).then(prices => {
      if (Object.keys(prices).length > 0) {
        setAllMarketPrices(prev => ({ ...prev, ...prices }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch OI data from contract
  const fetchOIData = useCallback(async () => {
    if (!marketInfo) return;
    try {
      const collateralIndex = parseInt(process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL?.match(/\d+/)?.[0] || '3', 10);
      console.log("[TradingTopBar] Fetching OI data for market:", marketInfo.marketIndex, "collateral:", collateralIndex);
      const result = await getOpenInterest(marketInfo.marketIndex, collateralIndex);
      if (result) {
        const longOI = parseFloat(result.long || "0") / 1e6;
        const shortOI = parseFloat(result.short || "0") / 1e6;
        const maxOI = parseFloat(result.max || "0") / 1e6;
        const total = longOI + shortOI;
        const longPct = total > 0 ? (longOI / total) * 100 : 0;
        const shortPct = total > 0 ? (shortOI / total) * 100 : 0;
        console.log("[TradingTopBar] OI from contract - Long:", longOI, "Short:", shortOI, "Max:", maxOI, "Raw:", result);
        setOiData({ longPct, shortPct, longVal: longOI, shortVal: shortOI, maxOI });
      }
    } catch (err) {
      console.warn('[TradingTopBar] Failed to fetch OI data:', err);
    }
  }, [marketInfo]);

  // Fetch funding rate from GraphQL borrowing API
  const fetchFundingRate = useCallback(async () => {
    if (!marketInfo) return;
    try {
      const { getPerpBorrowingFees } = await import('../functions/Contract');
      const collateralIndex = parseInt(process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL?.match(/\d+/)?.[0] || '3', 10);
      const borrowingData = await getPerpBorrowingFees(marketInfo.marketIndex, collateralIndex);
      if (borrowingData?.maxLeverage !== undefined && borrowingData?.maxLeverage !== null) {
        setContractMaxLeverage(borrowingData.maxLeverage);
      }
      if (borrowingData && (borrowingData.feesPerHourLong !== undefined || borrowingData.feesPerHourShort !== undefined)) {
        // Use the higher of the two rates (the side that's paying)
        const longRate = typeof borrowingData.feesPerHourLong === 'number' ? borrowingData.feesPerHourLong : 0;
        const shortRate = typeof borrowingData.feesPerHourShort === 'number' ? borrowingData.feesPerHourShort : 0;
        const dominantRate = Math.max(longRate, shortRate);
        // Use enough decimals to show significant digits for very small rates
        const pctValue = dominantRate * 100;
        let decimals = 6;
        if (pctValue > 0 && pctValue < 0.000001) {
          // Find first significant digit
          const s = pctValue.toExponential();
          const exp = parseInt(s.split('e')[1], 10);
          decimals = Math.min(Math.abs(exp) + 4, 18);
        }
        const formattedRate = `${pctValue.toFixed(decimals)}%/hr`;
        console.log("[TradingTopBar] Funding rate from API - Long:", longRate, "Short:", shortRate, "Display:", formattedRate);
        setFundingRate(formattedRate);
        setFundingRateRaw(pctValue); // store raw % value for subscript rendering

        // Also update OI from GraphQL if available
        if (borrowingData.oiLong !== undefined && borrowingData.oiShort !== undefined) {
          const longOI = borrowingData.oiLong / 1e6;
          const shortOI = borrowingData.oiShort / 1e6;
          const maxOI = (borrowingData.oiMax || 0) / 1e6;
          const total = longOI + shortOI;
          const longPct = total > 0 ? (longOI / total) * 100 : 0;
          const shortPct = total > 0 ? (shortOI / total) * 100 : 0;
          console.log("[TradingTopBar] OI from GraphQL - Long:", longOI, "Short:", shortOI, "Max:", maxOI);
          setOiData({ longPct, shortPct, longVal: longOI, shortVal: shortOI, maxOI });
        }
      } else {
        console.warn('[TradingTopBar] No funding rate data returned from API');
        setFundingRate(null);
      }
    } catch (err) {
      console.error('[TradingTopBar] Failed to fetch funding rate:', err);
      setFundingRate(null);
    }
  }, [marketInfo]);

  useEffect(() => {
    if (marketInfo) {
      // Drop the previous market's cap so the header falls back to this
      // market's config value rather than advertising the old one.
      setContractMaxLeverage(null);
      fetchOIData();
      fetchFundingRate();
      const oiInterval = setInterval(fetchOIData, 30000);
      const fundingInterval = setInterval(fetchFundingRate, 60000);
      return () => {
        clearInterval(oiInterval);
        clearInterval(fundingInterval);
      };
    }
  }, [marketInfo, fetchOIData, fetchFundingRate]);

  // Cleanup tooltip timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  // Generic RWA price fetcher
  const fetchRWAMarketPrices = useCallback(async (rwaMarketsList) => {
    const prices = {};

    const watchMarkets = rwaMarketsList.filter(m => m.watchBrandKey);
    const nonWatchMarkets = rwaMarketsList.filter(m => !m.watchBrandKey);

    // Fetch non-watch RWA markets (e.g., POKE, PSA-10) individually
    await Promise.all(
      nonWatchMarkets.map(async (market) => {
        try {
          if (market.id === 'psa-10') {
            const module = await import("../functions/Apiwithouttoken");
            const apiGet = module.apiGet || module.default?.apiGet;
            if (!apiGet) return;

            const response = await apiGet('psa10/24h-change');
            if (response?.success && response.data) {
              prices[market.id] = {
                price: response.data.currentValue || 0,
                change24h: response.data.change24h || 0,
              };
            }
            return;
          }

          if (market.id === 'poke' || market.chartType === 'poke') {
            const module = await import("../functions/Apiwithouttoken");
            const apiGet = module.apiGet || module.default?.apiGet;
            if (!apiGet) return;

            // POKE tracks the CardHedger POKE-INDEX. Compute 24h change from the
            // latest two daily price points, the same way real estate does.
            const response = await apiGet('pokeindex/7d');
            if (response?.success && response.data?.priceData?.length) {
              const sorted = [...response.data.priceData].sort((a, b) => new Date(b.date) - new Date(a.date));
              const todayPrice = sorted[0]?.price || 0;
              const yesterdayPrice = sorted[1]?.price || todayPrice;
              prices[market.id] = {
                price: todayPrice,
                change24h: yesterdayPrice > 0 ? ((todayPrice - yesterdayPrice) / yesterdayPrice) * 100 : 0,
              };
            }
          }
        } catch (error) {
          console.error(`Failed to fetch RWA price for ${market.id}:`, error);
        }
      })
    );

    // Fetch all watch market prices + 24h change via single GET /watch/24h-change
    if (watchMarkets.length > 0) {
      try {
        const module = await import("../functions/Apiwithouttoken");
        const apiGet = module.apiGet || module.default?.apiGet;
        if (apiGet) {
          const response = await apiGet('watch/24h-change');
          if (response?.success && Array.isArray(response.data)) {
            watchMarkets.forEach(m => {
              const brandData = response.data.find(b => b.brandKey === m.watchBrandKey);
              if (brandData) {
                prices[m.id] = {
                  price: brandData.currentPrice || 0,
                  change24h: brandData.change24h || 0,
                };
              }
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch watch market prices:', error);
      }
    }

    return prices;
  }, []);

  // Fetch all market prices whenever any market browser opens.
  //
  // Both browsers read the same `allMarketPrices`, so either entry point has
  // to prime it.
  useEffect(() => {
    const browserOpen = showMarketSelector || showCollectiblesSwitcher;
    if (!browserOpen) return;

    // Toggling between the selector and its list re-runs this effect. The data
    // is only worth refetching every so often, and CoinGecko is rate limited,
    // so reuse a recent result instead of hammering it on every toggle.
    if (Date.now() - lastPriceFetchRef.current < ALL_PRICES_TTL_MS) return;
    lastPriceFetchRef.current = Date.now();

    const fetchAllPrices = async () => {
      try {
        const cryptoPromise = (async () => {
          const coinIds = cryptoMarkets
            .map(m => m.coinGeckoId)
            .filter(Boolean)
            .join(',');
          if (!coinIds) return {};

          const response = await rateLimitedFetch(
            `https://api.coingecko.com/api/v3/simple/price?ids=${coinIds}&vs_currencies=usd&include_24hr_change=true`
          );
          const data = await response.json();

          const prices = {};
          cryptoMarkets.forEach(market => {
            if (market.coinGeckoId && data[market.coinGeckoId]) {
              prices[market.id] = {
                price: data[market.coinGeckoId].usd,
                change24h: data[market.coinGeckoId].usd_24h_change,
              };
            }
          });
          return prices;
        })();

        const rePromise = prefetchAllRealEstatePrices(realEstateMarkets);
        const rwaPromise = fetchRWAMarketPrices([...rwaMarkets, ...watchMarkets]);

        // Fetch stock & commodity prices from Nibiru candles API
        const equitiesPromise = (async () => {
          const equityMarkets = [...stockMarkets, ...commodityMarkets];
          if (equityMarkets.length === 0) return {};

          const prices = {};
          const now = Math.floor(Date.now() / 1000);
          const tenDaysAgo = now - 864000; // 10 days for weekends + holidays

          await Promise.allSettled(
            equityMarkets.map(async (market) => {
              try {
                const token = market.symbol;
                const url = `https://sai-candles.nibiru.fi/candles/udf/history?symbol=${token}&resolution=1D&from=${tenDaysAgo}&to=${now}`;
                const res = await fetch(url);
                if (!res.ok) return;
                const data = await res.json();

                // Extract close prices from response
                const closes = data?.c;
                const opens = data?.o;
                if (!closes || closes.length === 0) return;

                const latestClose = closes[closes.length - 1];
                let prevClose = latestClose;
                if (closes.length > 1) {
                  prevClose = closes[closes.length - 2];
                } else if (opens && opens.length > 0) {
                  prevClose = opens[opens.length - 1];
                }

                const change = prevClose !== 0 ? ((latestClose - prevClose) / prevClose) * 100 : 0;

                prices[market.id] = {
                  price: latestClose,
                  change24h: change,
                };
              } catch (err) {
                // Silently skip — individual market failure shouldn't block others
              }
            })
          );
          return prices;
        })();

        // Fetch PoC market prices from /poc/24h-change
        const pocPromise = (async () => {
          const pocMkts = getPocMarkets();
          if (pocMkts.length === 0) return {};
          try {
            const module = await import("../functions/Apiwithouttoken");
            const apiGet = module.apiGet || module.default?.apiGet;
            if (!apiGet) return {};
            const response = await apiGet('poc/24h-change');
            if (!response?.success || !Array.isArray(response.data)) return {};
            const prices = {};
            pocMkts.forEach(m => {
              const d = response.data.find(p => p.marketKey === m.pocMarketKey);
              if (d) prices[m.id] = { price: d.currentPrice || 0, change24h: d.change24h || 0 };
            });
            return prices;
          } catch { return {}; }
        })();

        const [cryptoPrices, rePrices, rwaPrices, equityPrices, pocPrices] = await Promise.all([
          cryptoPromise,
          rePromise,
          rwaPromise,
          equitiesPromise,
          pocPromise,
        ]);

        setAllMarketPrices(prev => ({ ...prev, ...rePrices, ...cryptoPrices, ...rwaPrices, ...equityPrices, ...pocPrices }));
      } catch (error) {
        console.error('Failed to fetch dropdown market prices:', error);
        // Let the next open retry immediately rather than serving nothing
        // until the TTL lapses.
        lastPriceFetchRef.current = 0;
      }
    };

    fetchAllPrices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMarketSelector, showCollectiblesSwitcher, fetchRWAMarketPrices]);

  // Navigate to selected market
  const handleMarketSelect = (market) => {
    // Picking a market has to dismiss whichever browser is behind it. Without
    // this the browser stayed mounted over the market it had just navigated
    // to — the Pokédex device sitting on top of a real-estate page.
    setShowMarketSelector(false);
    setShowCollectiblesSwitcher(false);

    // PoC preview markets always route to /poc, regardless of their display
    // category (e.g. PSA-10 is grouped under RWA but has no on-chain contract).
    if (market.pocMode) {
      navigate(`/perps/markets/poc/${market.id}`);
    } else if (market.category === MARKET_CATEGORIES.REAL_ESTATE) {
      const urlId = market.id.replace(/-/g, '');
      navigate(`/perps/markets/realestate/${urlId}`);
    } else if (market.watchBrandKey || market.category === MARKET_CATEGORIES.WATCHES) {
      const urlId = market.id.replace(/-/g, '');
      navigate(`/perps/markets/watch/${urlId}`);
    } else if (market.category === MARKET_CATEGORIES.RWA) {
      navigate(`/perps/markets/rwa/${market.id}`);
    } else if (market.category === MARKET_CATEGORIES.STOCKS) {
      navigate(`/perps/markets/stocks/${market.id}`);
    } else if (market.category === MARKET_CATEGORIES.COMMODITIES) {
      navigate(`/perps/markets/commodities/${market.id}`);
    } else {
      navigate(`/perps/markets/crypto/${market.id}`);
    }
  };

  const handleToggleFavorite = (e, marketId) => {
    e.stopPropagation();
    const newFavs = toggleFavorite(marketId);
    setFavorites([...newFavs]);
  };

  // Price change detection
  const handlePriceChange = useCallback((newPrice, oldPrice) => {
    if (!newPrice || !oldPrice || newPrice === oldPrice) return;

    window.dispatchEvent(new CustomEvent('priceChangedCheckHits', {
      detail: {
        newPrice,
        oldPrice,
        priceDirection: newPrice > oldPrice ? 'up' : 'down',
        timestamp: Date.now()
      }
    }));
  }, []);

  useEffect(() => {
    const currentPrice = price;
    const previousPrice = previousPriceRef.current;

    if (currentPrice && previousPrice && currentPrice !== previousPrice) {
      handlePriceChange(currentPrice, previousPrice);
    }

    previousPriceRef.current = currentPrice;
  }, [price, handlePriceChange]);

  const getPrice = useCallback(async () => {
    // PoC markets have no on-chain token, so there is no oracle price to read —
    // getMarketData fills it from the backend instead. Live markets (PSA-10
    // included) fall through and query the oracle.
    const mkt = getMarketByTokenIndex(tokenIndex);
    if (mkt?.pocMode) return;

    let parsedIndex = tokenIndex;
    if (typeof tokenIndex === 'string') {
      const tokenIndexMatch = tokenIndex.match(/TokenIndex\((\d+)\)/);
      if (tokenIndexMatch && tokenIndexMatch[1]) {
        parsedIndex = parseInt(tokenIndexMatch[1], 10);
      }
    }

    const msg = {
      get_price: {
        index: parsedIndex,
      },
    };

    try {
      const contracts = getContractAddresses();
      const res = await queryContract(
        contracts.oracle,
        msg,
        nibiru.endptTm
      );

      if (res && typeof res === 'object') {
        if (res.data && res.data.price) {
          dispatch(setCurrentPrice(res.data.price));
        } else if (res.price) {
          dispatch(setCurrentPrice(res.price));
        }
      }
    } catch (error) {
      console.error("Failed to fetch price:", error);
    }
  }, [tokenIndex, dispatch]);

  const getCoinIdFromIndex = useCallback((index) => {
    return getCoinId(index);
  }, []);

  const isCityMarketCheck = useCallback((coinId) => {
    return isCityMarket(coinId);
  }, []);

  const fetchPocMarketData = useCallback(async (market) => {
    try {
      const module = await import("../functions/Apiwithouttoken");
      const apiGet = module.apiGet || module.default?.apiGet;
      if (!apiGet) throw new Error("API module not available");

      const response = await apiGet(`poc/market/${market.pocMarketKey}`);
      if (response?.success && response.data) {
        setMarketData({
          price: response.data.currentPrice || 0,
          change24h: response.data.change24h || 0,
          volume24h: null,
        });
        dispatch(setCurrentPrice(response.data.currentPrice));
      } else {
        setMarketData({ price: 0, change24h: 0, volume24h: null });
      }
    } catch (error) {
      console.error(`Error fetching PoC data for ${market.id}:`, error);
      setMarketData({ price: 0, change24h: 0, volume24h: null });
    }
  }, [dispatch]);

  const fetchPsa10MarketData = useCallback(async () => {
    try {
      const module = await import("../functions/Apiwithouttoken");
      const apiGet = module.apiGet || module.default?.apiGet;

      if (!apiGet) throw new Error("API module not available");

      // PSA-10 tracks the PriceDepth PDI-PSA10 index (psa10snapshots) — the same
      // source the oracle is fed from, and the same source as the chart. The
      // backend computes the day-over-day change from the last two daily points.
      const response = await apiGet('psa10/24h-change');
      if (response?.success && response.data) {
        setMarketData({
          price: response.data.currentValue || 0,
          change24h: response.data.change24h || 0,
          volume24h: null,
        });
        return;
      }
      setMarketData({ price: 0, change24h: 0, volume24h: null });
    } catch (error) {
      console.error('Error fetching PSA-10 market data:', error);
      setMarketData({ price: 0, change24h: 0, volume24h: null });
    }
  }, []);

  const fetchPokemonMarketData = useCallback(async () => {
    try {
      const module = await import("../functions/Apiwithouttoken");
      const apiGet = module.apiGet || module.default?.apiGet;

      if (!apiGet) throw new Error("API module not available");

      // POKE market tracks the CardHedger POKE-INDEX (pokeindexes collection) — the
      // same source as the chart. Compute the 24h change from the latest two daily
      // price points (today vs yesterday), the same way real estate does.
      const response = await apiGet('pokeindex/7d');

      if (response?.success && response.data?.priceData?.length) {
        const sorted = [...response.data.priceData].sort((a, b) => new Date(b.date) - new Date(a.date));
        const todayPrice = sorted[0]?.price || 0;
        const yesterdayPrice = sorted[1]?.price || todayPrice;
        const change24h = yesterdayPrice > 0
          ? ((todayPrice - yesterdayPrice) / yesterdayPrice) * 100
          : 0;
        setMarketData({
          price: todayPrice,
          change24h,
          volume24h: null,
        });
      } else {
        throw new Error('No data in response');
      }
    } catch (error) {
      console.error('Error fetching POKE-INDEX data:', error);
      setMarketData({ price: 0, change24h: 0, volume24h: null });
    }
  }, []);

  const fetchCityMarketData = useCallback(async (cityName) => {
    try {
      const market = getMarketById(cityName);
      if (!market) {
        setMarketData({ price: 0, change24h: 0, volume24h: null });
        return;
      }

      try {
        const module = await import("../functions/Apiwithouttoken");
        const api = module.api || module.default?.api;

        if (api) {
          const response = await api('parcl/city-data', { cityId: cityName });

          if (response && response.success && response.data) {
            const { PriceData } = response.data;
            const sortedPriceData = [...PriceData].sort((a, b) => new Date(b.date) - new Date(a.date));
            const todayPrice = sortedPriceData[0]?.price || 0;
            const yesterdayPrice = sortedPriceData[1]?.price || todayPrice;
            const change24h = yesterdayPrice > 0
              ? ((todayPrice - yesterdayPrice) / yesterdayPrice) * 100
              : 0;

            setMarketData({
              price: todayPrice,
              change24h: change24h,
              volume24h: null,
            });
            return;
          }
        }
      } catch (internalApiError) {
        console.warn(`Internal API failed for ${cityName}:`, internalApiError);
      }

      const parclSymbol = market.marketCode;
      if (!parclSymbol) {
        setMarketData({ price: 0, change24h: 0, volume24h: null });
        return;
      }

      try {
        const searchUrl = `https://express-prod.parcl-api.com/v1/market/search?q=&sort=volume&order=desc&baseCategory=all-markets&window=1d`;
        const searchResponse = await fetch(searchUrl);
        if (!searchResponse.ok) throw new Error(`Parcl search API failed: ${searchResponse.status}`);

        const searchData = await searchResponse.json();
        const foundMarket = searchData.markets?.find(m => m.symbol === parclSymbol);

        if (foundMarket) {
          let change24h = foundMarket.indexPriceTrend || foundMarket.indexPriceTrend1d || foundMarket.priceChange1d || 0;
          const currentPrice = foundMarket.indexPrice || foundMarket.price || 0;

          setMarketData({
            price: currentPrice,
            change24h: change24h,
            volume24h: null,
          });
          return;
        }
      } catch (searchError) {
        console.warn(`Parcl search API failed:`, searchError);
      }

      setMarketData({ price: 0, change24h: 0, volume24h: null });
    } catch (error) {
      console.error(`Error fetching ${cityName} data:`, error);
      setMarketData({ price: 0, change24h: 0, volume24h: null });
    }
  }, []);

  const fetchWatchMarketData = useCallback(async (market) => {
    try {
      const module = await import("../functions/Apiwithouttoken");
      const apiGet = module.apiGet || module.default?.apiGet;
      if (!apiGet) throw new Error("API module not available");

      const response = await apiGet('watch/24h-change');
      if (response?.success && Array.isArray(response.data)) {
        const brandData = response.data.find(b => b.brandKey === market.watchBrandKey);
        if (brandData) {
          setMarketData({
            price: brandData.currentPrice || 0,
            change24h: brandData.change24h || 0,
            volume24h: null,
          });
          return;
        }
      }
      setMarketData({ price: 0, change24h: 0, volume24h: null });
    } catch (error) {
      console.error(`Error fetching watch data for ${market.id}:`, error);
      setMarketData({ price: 0, change24h: 0, volume24h: null });
    }
  }, []);

  const getMarketData = useCallback(async () => {
    try {
      const market = getMarketByTokenIndex(tokenIndex);

      // PoC markets — fetch from backend /poc endpoint
      if (market?.pocMode) {
        await fetchPocMarketData(market);
        return;
      }

      // Stocks & Commodities — fetch from Nibiru candles API
      if (market && (market.category === MARKET_CATEGORIES.STOCKS || market.category === MARKET_CATEGORIES.COMMODITIES)) {
        try {
          const token = market.symbol;
          const now = Math.floor(Date.now() / 1000);
          const tenDaysAgo = now - 864000; // 10 days for weekends + holidays
          const url = `https://sai-candles.nibiru.fi/candles/udf/history?symbol=${token}&resolution=1D&from=${tenDaysAgo}&to=${now}`;
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();

          const closes = data?.c;
          const opens = data?.o;
          const volumes = data?.v;

          if (closes && closes.length > 0) {
            const latestClose = closes[closes.length - 1];
            let prevClose = latestClose;
            if (closes.length > 1) prevClose = closes[closes.length - 2];
            else if (opens && opens.length > 0) prevClose = opens[opens.length - 1];

            const change = prevClose !== 0 ? ((latestClose - prevClose) / prevClose) * 100 : 0;
            const volume = volumes && volumes.length > 0 ? volumes[volumes.length - 1] : 0;

            setMarketData({
              price: latestClose,
              change24h: change,
              volume24h: volume > 1000000 ? `$${(volume / 1000000).toFixed(2)}M` : volume > 1000 ? `$${(volume / 1000).toFixed(2)}K` : `$${volume.toFixed(2)}`,
            });
          } else {
            setMarketData({ price: 0, change24h: 0, volume24h: "$0" });
          }
        } catch (err) {
          console.error(`Failed to fetch candle data for ${market?.symbol}:`, err);
          setMarketData({ price: 0, change24h: 0, volume24h: "$0" });
        }
        return;
      }

      if (market && market.id === 'poke') {
        await fetchPokemonMarketData();
        return;
      }

      if (market && market.id === 'psa-10') {
        await fetchPsa10MarketData();
        return;
      }

      if (market && (market.category === MARKET_CATEGORIES.WATCHES || market.watchBrandKey)) {
        await fetchWatchMarketData(market);
        return;
      }

      const coinId = getCoinIdFromIndex(tokenIndex);

      if (isCityMarketCheck(coinId)) {
        await fetchCityMarketData(coinId);
        return;
      }

      let coinGeckoId = market?.coinGeckoId;

      if (!coinGeckoId) {
        const legacyMap = {
          'bitcoin': 'bitcoin',
          'ethereum': 'ethereum',
          'cosmos': 'cosmos',
        };
        coinGeckoId = legacyMap[coinId] || coinId;
      }

      if (!coinGeckoId || isCityMarketCheck(coinId)) return;

      const apiUrl = `https://api.coingecko.com/api/v3/coins/${coinGeckoId}`;

      const response = await rateLimitedFetch(apiUrl);

      if (!response.ok) {
        if (response.status === 429) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          const retryResponse = await rateLimitedFetch(apiUrl);
          if (!retryResponse.ok) {
            if (retryResponse.status === 429) {
              await new Promise(resolve => setTimeout(resolve, 5000));
              const finalRetryResponse = await rateLimitedFetch(apiUrl);
              if (!finalRetryResponse.ok) throw new Error(`Network response was not ok: ${finalRetryResponse.status}`);
              const finalRetryData = await finalRetryResponse.json();
              processAndSetMarketData(finalRetryData, coinId);
              return;
            }
            throw new Error(`Network response was not ok: ${retryResponse.status}`);
          }
          const retryData = await retryResponse.json();
          processAndSetMarketData(retryData, coinId);
          return;
        }
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      const data = await response.json();
      processAndSetMarketData(data, coinId);
    } catch (error) {
      console.error("Error fetching market data:", error);
      setMarketData({ price: 0, change24h: 0, volume24h: "$0M" });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenIndex, getCoinIdFromIndex, fetchCityMarketData, fetchPokemonMarketData, fetchPsa10MarketData, fetchWatchMarketData, fetchPocMarketData, isCityMarketCheck]);

  const processAndSetMarketData = useCallback((data) => {
    const currentPrice = data.market_data?.current_price?.usd || 0;
    const priceChange24h = data.market_data?.price_change_percentage_24h || 0;
    const volume24h = data.market_data?.total_volume?.usd
      ? `$${(data.market_data.total_volume.usd / 1000000).toFixed(2)}M`
      : "$0M";

    setMarketData({
      price: currentPrice,
      change24h: priceChange24h,
      volume24h: volume24h
    });
  }, []);

  const checkIfRealEstate = useCallback(() => {
    const market = getMarketByTokenIndex(tokenIndex);
    setMarketInfo(market);
    const isRE = market?.category === MARKET_CATEGORIES.REAL_ESTATE;
    setIsRealEstate(isRE);
    return isRE;
  }, [tokenIndex]);

  useEffect(() => {
    setMarketData({ price: 0, change24h: 0, volume24h: "$0M" });
    setOiData({ longPct: 0, shortPct: 0, longVal: 0, shortVal: 0, maxOI: 0 });
    setFundingRate(null);

    checkIfRealEstate();
    getPrice();
    getMarketData();

    const priceInterval = setInterval(getPrice, 10000);
    // Oracle-based markets (RE, watches, poke) update daily — refresh market data (incl. 24h change) every 60s
    // Crypto markets use CoinGecko which is rate-limited, so refresh every 5 min
    const market = getMarketByTokenIndex(tokenIndex);
    const isOracleMarket = market?.category === MARKET_CATEGORIES.REAL_ESTATE ||
                           market?.category === MARKET_CATEGORIES.WATCHES ||
                           market?.id === 'poke';
    const marketDataInterval = setInterval(getMarketData, isOracleMarket ? 60000 : 300000);

    return () => {
      clearInterval(priceInterval);
      clearInterval(marketDataInterval);
    };
  }, [tokenIndex, getPrice, getMarketData, checkIfRealEstate]);

  const isLowPriceToken = marketInfo && lowPriceTokenIds.has(marketInfo.id);
  // For stocks/commodities, prefer candles API price if oracle price is 0
  const displayPrice = (isStockMarket || isCommodityMarket) && (!price || Number(price) === 0) && marketData.price
    ? marketData.price
    : price;
  const formattedPrice = (isStockMarket || isCommodityMarket) && Number(displayPrice) >= 1
    ? Number(displayPrice).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : formatPriceWithSubscript(displayPrice, isLowPriceToken);

  // Get market icon URL
  const getMarketIcon = () => {
    if (marketInfo?.iconPath) return getImageUrl(marketInfo.iconPath);
    if (marketInfo?.coinGeckoId) return getCoinGeckoImageUrl(marketInfo.coinGeckoId, marketInfo.id);
    return null;
  };

  const marketIconUrl = getMarketIcon();
  const isFav = marketInfo ? favorites.includes(marketInfo.id) : false;
  
  // Every countdown pill moved into `PriceUpdateBadge`, which renders over the
  // chart; none of them are formatted here any more.

  // Tooltip helpers
  const showTooltip = (e, content) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      content,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const hideTooltip = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltip({ show: false, content: '', x: 0, y: 0 });
    }, 100);
  };

  const calculateAnnualRate = (hourlyRateStr) => {
    if (!hourlyRateStr || hourlyRateStr === '--') return '--';
    const cleanRate = hourlyRateStr.replace('/hr', '').replace('%', '');
    const hourlyRate = parseFloat(cleanRate);
    if (isNaN(hourlyRate)) return '--';
    const annualRate = hourlyRate * 24 * 365;
    // Show enough precision for tiny rates
    let decimals = 2;
    if (annualRate > 0 && annualRate < 0.01) {
      const s = annualRate.toExponential();
      const exp = parseInt(s.split('e')[1], 10);
      decimals = Math.min(Math.abs(exp) + 2, 12);
    }
    return `${annualRate.toFixed(decimals)}%`;
  };

  return (
    /* No top margin: the bar is the first thing in the left panel, and the
       right panel's order form starts flush at the top of the same row — a
       margin here sits the whole left column lower than the right. The gap
       under the (now fixed-footer) marquee is the row's own `pt-5`. */
    <div className="w-full">
      {/* The oracle markets' "Next Price Update" pill now renders as an overlay
          pinned inside the chart (`PriceUpdateBadge`) instead of claiming a
          full row here. */}

      {/* The scheduled-market countdown (stocks & commodities) moved out with
          the oracle ones: it renders inside the chart as `PriceUpdateBadge`
          rather than claiming a full row above the bar for a 28px pill. */}

      {/* Single unified container: Top (market info) + Divider + Bottom (OI, funding, max OI) */}
      <div
        className="w-full"
        style={{
          borderRadius: '18px',
          /* 16px of padding around a 16px gap around two rows that each
             carried their own stacked label and value made this bar taller
             than the order ticket's first three controls put together — all of
             it above the chart, which is the thing being looked at. */
          padding: '8px 14px',
          background: 'var(--surface)',
          /* A card carries `--card` and no border: the shadow is the edge. The
             old `4px 4px 40px rgba(187,195,206,.1)` was a 40px bloom at 10%,
             which on a white page is not an edge at all. */
          boxShadow: 'var(--card)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {/* === Top section: Market selector + Price stats === */}

        {/* Mobile layout */}
        <div className="md:hidden">
          <div className="flex items-center gap-3">
            {/* Market selector tab */}
            <div className="relative flex-shrink-0" ref={dropdownRef} data-market-dropdown>
              <div
                className="ce-pill flex items-center gap-2 cursor-pointer px-3 py-2 min-w-[120px]"
                onClick={openMarketBrowser}
                role="button"
                aria-label="Change market"
              >
                <img
                  src={isFav ? highlightedStarIcon : unhighlightedStarIcon}
                  alt=""
                  className="cursor-pointer"
                  style={{ width: '22px', height: '22px', borderRadius: '0.92px', flexShrink: 0 }}
                  onClick={(e) => { e.stopPropagation(); if (marketInfo) handleToggleFavorite(e, marketInfo.id); }}
                />
                {marketIconUrl && (
                  marketInfo?.category === MARKET_CATEGORIES.WATCHES ? (
                    <span className="ce-logo-tile w-6 h-6">
                      <img src={marketIconUrl} alt={marketInfo?.symbol || 'Coin'} className="w-4 h-4" />
                    </span>
                  ) : (
                    <img
                      src={marketIconUrl}
                      alt={marketInfo?.symbol || 'Coin'}
                      className={`${isRealEstate ? 'w-6 h-4 rounded-[2px] object-cover' : 'w-5 h-5 rounded-full object-cover'}`}
                    />
                  )
                )}
                <span className="text-sm font-semibold text-gray-800">{marketInfo?.symbol || 'SYMBOL'}</span>
                {/* Reads as the action it now performs. The chevron went with
                    the dropdown — nothing drops down from here any more. */}
                <span className="ce-chip ce-chip-accent whitespace-nowrap" style={{ fontSize: 11, padding: '5px 10px' }}>Change</span>
              </div>

            </div>

            {/* Stats row - mobile (horizontally scrollable without visible scrollbar) */}
            <div className="mobile-stats-scroll flex-1 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
              <style>{`.mobile-stats-scroll::-webkit-scrollbar { display: none; }`}</style>
              <div className="flex items-center gap-3" style={{ minWidth: 'max-content' }}>
                <div
                  className="text-center relative"
                  onMouseEnter={(e) => showTooltip(e, 'This is the oracle price, the official price used for perp trading and order execution. Coded Estate reports the price from an onchain oracle that aggregates prices from multiple exchanges.')}
                  onMouseLeave={hideTooltip}
                >
                  <div className="text-[10px] text-gray-400 mb-0.5 cursor-help whitespace-nowrap">Market Price</div>
                  {/* No glow. Weight and hue carry "this is the live oracle
                      price"; a text-shadow on it read as a rendering fault. */}
                  <div className="ce-figure text-sm font-bold whitespace-nowrap" style={{ color: "var(--ink)" }}>
                    <NumberTicker text={formattedPrice} prefix="$" duration={0.55} stagger={0.03} blur />
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] text-gray-400 mb-0.5 whitespace-nowrap">24h Change</div>
                  <div className={`ce-figure text-sm font-bold whitespace-nowrap ${marketData.change24h >= 0 ? 'text-[#5DBE89]' : 'text-[#EB4245]'}`}>
                    <NumberTicker
                      text={`${marketData.change24h >= 0 ? '+' : ''}${(marketData.change24h || 0).toFixed(2)}%`}
                      duration={0.55}
                      stagger={0.03}
                    />
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] text-gray-400 mb-0.5 whitespace-nowrap">24h Volume</div>
                  <div className="ce-figure text-sm font-bold text-gray-800 whitespace-nowrap">
                    {marketData.volume24h !== null && marketData.volume24h !== undefined
                      ? <NumberTicker text={formatCurrencyWithSuffix(marketData.volume24h)} duration={0.7} stagger={0.03} />
                      : '--'}
                  </div>
                </div>
                      {/* Asset information - mobile */}
                {marketInfo?.assetInfo && (
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={() => setAssetInfoOpen(!assetInfoOpen)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        fontFamily: '"SF Pro", -apple-system, BlinkMacSystemFont, sans-serif',
                        fontSize: '11px',
                        fontWeight: 400,
                        lineHeight: '16px',
                        color: '#8A8A8A',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                        <circle cx="8" cy="8" r="7" stroke="#8A8A8A" strokeWidth="1.5"/>
                        <path d="M8 7v5" stroke="#8A8A8A" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="8" cy="4.5" r="0.75" fill="#8A8A8A"/>
                      </svg>
                      Asset info
                    </button>

                    {assetInfoOpen && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'fixed',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: 'calc(100vw - 32px)',
                          maxWidth: '300px',
                          background: '#FFFFFF',
                          borderRadius: '12px',
                          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                          padding: '16px',
                          zIndex: 200,
                          border: '1px solid rgba(0,0,0,0.06)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <div style={{ fontFamily: 'General Sans', fontSize: '13px', fontWeight: 600, color: '#1A1A1A', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="8" cy="8" r="7" stroke="#5B1DEE" strokeWidth="1.5"/>
                              <path d="M8 7v5" stroke="#5B1DEE" strokeWidth="1.5" strokeLinecap="round"/>
                              <circle cx="8" cy="4.5" r="0.75" fill="#5B1DEE"/>
                            </svg>
                            {marketInfo.name} — Asset Info
                          </div>
                          <button
                            onClick={() => setAssetInfoOpen(false)}
                            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                          >
                            <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                              <path d="M1 1L13 13M13 1L1 13" stroke="#667085" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div>
                            <div style={{ fontFamily: 'General Sans', fontSize: '10px', fontWeight: 600, color: '#5B1DEE', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>What you're trading</div>
                            <div style={{ fontFamily: 'General Sans', fontSize: '12px', color: '#3A3A3A', lineHeight: '1.5' }}>{marketInfo.assetInfo.what}</div>
                          </div>
                          <div style={{ height: '1px', background: 'rgba(0,0,0,0.06)' }} />
                          <div>
                            <div style={{ fontFamily: 'General Sans', fontSize: '10px', fontWeight: 600, color: '#5B1DEE', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Price source</div>
                            <div style={{ fontFamily: 'General Sans', fontSize: '12px', color: '#3A3A3A', lineHeight: '1.5' }}>{marketInfo.assetInfo.priceDriver}</div>
                          </div>
                          <div style={{ height: '1px', background: 'rgba(0,0,0,0.06)' }} />
                          <div>
                            <div style={{ fontFamily: 'General Sans', fontSize: '10px', fontWeight: 600, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Things to know</div>
                            <div style={{ fontFamily: 'General Sans', fontSize: '12px', color: '#3A3A3A', lineHeight: '1.5' }}>{marketInfo.assetInfo.risks}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop layout - top section.

            `space-around` used to distribute these, which put half a gap at each
            end and full gaps between — so the three stats bunched left of centre
            and a void opened before "Asset information". The row is now three
            explicit zones: the market button at its natural width, the stats
            sharing the space between, and asset info pinned right. */}
        <div className="hidden md:flex items-center" style={{ justifyContent: 'flex-start', gap: '20px', flexWrap: 'nowrap' }}>
          {/* Market selector tab.
              Allowed to shrink, and the first thing to give up width: the
              market name inside it truncates cleanly, where the stats beside it
              are numbers that cannot be cut. Pinned at `flex-shrink-0` it
              overflowed the row and the volume figure printed underneath
              "Asset information". */}
          <div className="relative" ref={dropdownRef} data-market-dropdown style={{ minWidth: 0, flexShrink: 1 }}>
            <div className="flex items-center" style={{ gap: 10, minWidth: 0 }}>
              <img
                src={isFav ? highlightedStarIcon : unhighlightedStarIcon}
                alt=""
                className="cursor-pointer"
                style={{ width: '22px', height: '22px', flexShrink: 0 }}
                onClick={(e) => { e.stopPropagation(); if (marketInfo) handleToggleFavorite(e, marketInfo.id); }}
              />
              <MarketIdentity
                market={marketInfo}
                maxLeverage={resolveMaxLeverage(contractMaxLeverage, marketInfo?.maxLeverage || 100)}
                onOpen={openMarketBrowser}
                compact
              />
            </div>
          </div>

          {/* The three price stats share the space between the market button
              and asset info, each taking an equal slice.
              No `min-width: 0` here — that let the container shrink past its own
              content, and the three figures inside it (which have a floor of
              their own) simply overflowed to the right. It now holds its
              min-content width and the market button yields instead. */}
          {/* `min-width: 0` + `overflow: hidden`: at a narrow window this group
              clips rather than letting "Max OI" run over "Asset information". */}
          <div className="flex items-center" style={{ flex: '1 1 auto', gap: '20px', minWidth: 0, overflow: 'hidden' }}>

          {/* Market Price */}
          <div
            className="flex flex-col items-center justify-center relative"
            style={{ flex: '1 1 0', minWidth: '90px', maxWidth: '220px', gap: '2px' }}
            onMouseEnter={(e) => showTooltip(e, 'This is the oracle price, the official price used for perp trading and order execution. Coded Estate reports the price from an onchain oracle that aggregates prices from multiple exchanges.')}
            onMouseLeave={hideTooltip}
          >
            <div style={{ fontFamily: "General Sans", fontSize: 10.5, fontWeight: 500, color: "var(--ink-3)", cursor: "help", lineHeight: 1.2 }}>Market Price</div>
            {/* Oracle price arrives as a stream; the digits roll to the new
                value so a trader sees the change happen instead of catching a
                number that silently swapped. */}
            {/* Plain ink, not green. The oracle price is a *quantity*; green
                is the direction language the 24h chip owns, and printing a
                permanently green price beside a red change said "up" and
                "down" about the same market in the same breath. */}
            <div className="ce-figure text-[16px] font-bold leading-tight" style={{ color: "var(--ink)" }}>
              <NumberTicker text={formattedPrice} prefix="$" duration={0.55} stagger={0.03} blur />
            </div>
          </div>

          {/* 24h Change */}
          <div
            className="flex flex-col items-center justify-center"
            style={{ flex: '1 1 0', minWidth: '90px', maxWidth: '220px', gap: '2px' }}
          >
            <div style={{ fontFamily: "General Sans", fontSize: 10.5, fontWeight: 500, color: "var(--ink-3)", lineHeight: 1.2 }}>24h Change</div>
            {/* The one figure on this bar that reads as a *status* rather than
                a quantity, so it gets a tinted chip and the rest stay bare.
                The arrow carries the sign as well as the colour — red/green
                alone is the one pair a colour-blind trader cannot separate. */}
            <div
              className={`ce-chip ce-figure ${marketData.change24h >= 0 ? 'ce-chip-up' : 'ce-chip-down'}`}
              style={{ fontSize: 13 }}
            >
              <span aria-hidden="true">{marketData.change24h >= 0 ? '↑' : '↓'}</span>
              <NumberTicker
                text={`${marketData.change24h >= 0 ? '+' : ''}${(marketData.change24h || 0).toFixed(2)}%`}
                duration={0.55}
                stagger={0.03}
              />
            </div>
          </div>

          {/* 24h Volume */}
          <div
            className="flex flex-col items-center justify-center"
            style={{ flex: '1 1 0', minWidth: '90px', maxWidth: '220px', gap: '2px' }}
          >
            <div style={{ fontFamily: "General Sans", fontSize: 10.5, fontWeight: 500, color: "var(--ink-3)", lineHeight: 1.2 }}>24h Volume</div>
            <div className="ce-figure text-[16px] font-bold leading-tight" style={{ color: "var(--ink)" }}>
              {marketData.volume24h !== null && marketData.volume24h !== undefined
                ? <NumberTicker text={formatCurrencyWithSuffix(marketData.volume24h)} duration={0.7} stagger={0.03} />
                : '--'}
            </div>
          </div>

          {/* The countdown for markets that do not price continuously.
              It belongs on this row, beside the price it is counting down to —
              a trader reading "$605.55" needs to know that figure is six hours
              old before they read anything else on the bar. Down on the stats
              row it sat below and to the right of the number it qualifies.
              `position: static` opts out of the component's default absolute
              placement. */}
          <PriceUpdateBadge tokenIndex={tokenIndex} stacked style={{ position: "static", flexShrink: 0 }} />

          </div>

        </div>

        {/* Desktop layout - bottom section. Same fix as the row above: the OI
            block takes the slack and its bar grows into it, while Funding Rate
            and Max Open Interest travel together on the right instead of being
            stranded either side of a gap. */}
        <div className="hidden md:flex items-center" style={{ justifyContent: 'flex-start', gap: '20px' }}>
          {/* Open Interest section with inline bar */}
          <div
            className="flex items-center relative"
            style={{ minWidth: 0, flex: '1 1 auto', gap: 10, overflow: 'hidden' }}
            onMouseEnter={(e) => showTooltip(e, 'The combined size of all long and short positions in this market.')}
            onMouseLeave={hideTooltip}
          >
            {/* Label beside the figures rather than stacked over them. Each of
                these four blocks used to spend a whole line on a caption and
                another on its value, which is two lines of bar for one line of
                data. `overflow: hidden` is the other half of the fix: the OI
                figures are `nowrap`, so at narrow widths they used to run out
                of this block and print on top of the funding rate. */}
            <span style={{ fontFamily: 'General Sans', fontSize: '11.5px', color: '#98A2B3', cursor: 'help', whiteSpace: 'nowrap', flexShrink: 0 }}>Open Interest</span>
            <div className="flex items-center" style={{ gap: 'clamp(6px, 1.5vw, 14px)', minWidth: 0, flex: '1 1 auto' }}>
              <span className="flex items-center" style={{ gap: '3px', flexShrink: 0 }}>
                <img src={upArrowIcon} alt="" style={{ width: '10px', height: '10px' }} />
                <span style={{ fontFamily: 'General Sans', fontSize: 'clamp(11px, 1vw, 13px)', fontWeight: 600, color: '#30AA67' }}>{oiData.longPct.toFixed(0)}%</span>
                <span style={{ fontFamily: 'General Sans', fontSize: 'clamp(10px, 1vw, 13px)', color: '#667085' }}>{formatOIValue(oiData.longVal)}</span>
              </span>
              {/* Inline OI Bar - only show when there's actual OI data */}
              {(oiData.longVal > 0 || oiData.shortVal > 0) && (
                /* The bar is the only part of this block that can be any
                   width and still say the same thing, so it is what shrinks
                   when the row is tight — the percentages and dollar figures
                   either read or they do not. */
                /* A recessed trough holding two raised fills, same as every
                   other track on the page: 6px tall with 1px of padding, so
                   the fills sit *in* the bar rather than being the bar. */
                <div style={{ flex: '1 1 auto', minWidth: '24px', maxWidth: '260px', display: 'flex', height: '6px', padding: '1px', borderRadius: '999px', background: 'var(--surface-2)', boxShadow: 'var(--inset-sm)', overflow: 'hidden', gap: '2px' }}>
                  <div style={{ width: `${oiData.longPct}%`, minWidth: oiData.longPct > 0 ? '2px' : '0', background: 'linear-gradient(180deg, #2BD98A, #10C46A)', borderRadius: '999px', transition: 'width 0.3s ease' }} />
                  <div style={{ width: `${oiData.shortPct}%`, minWidth: oiData.shortPct > 0 ? '2px' : '0', background: 'linear-gradient(180deg, #FF6478, #E4474A)', borderRadius: '999px', transition: 'width 0.3s ease' }} />
                </div>
              )}
              <span className="flex items-center" style={{ gap: '3px', flexShrink: 0 }}>
                <img src={downArrowIcon} alt="" style={{ width: '10px', height: '10px' }} />
                <span style={{ fontFamily: 'General Sans', fontSize: 'clamp(11px, 1vw, 13px)', fontWeight: 600, color: '#E4474A' }}>{oiData.shortPct.toFixed(0)}%</span>
                <span style={{ fontFamily: 'General Sans', fontSize: 'clamp(10px, 1vw, 13px)', color: '#667085' }}>{formatOIValue(oiData.shortVal)}</span>
              </span>
            </div>
          </div>

          {/* Funding Rate */}
          <div
            className="flex items-center relative"
            style={{ flexShrink: 0, gap: 6 }}
            onMouseEnter={(e) => {
              const annualRate = fundingRate ? calculateAnnualRate(fundingRate) : '--';
              showTooltip(e, `Annual Rate:\n${annualRate} /year\n\nA time-based fee paid by positions in the direction of the imbalance in long and short open interest. If net open interest is long, these are paid by longs, and if net open interest is short, the fees are paid by shorts. Fees are paid to the protocol.`);
            }}
            onMouseLeave={hideTooltip}
          >
            <span style={{ fontFamily: 'General Sans', fontSize: '11.5px', color: '#98A2B3', cursor: 'help', whiteSpace: 'nowrap' }}>Funding Rate</span>
            <span style={{ fontFamily: 'General Sans', fontSize: 'clamp(11px, 1vw, 13px)', fontWeight: 700, color: '#1A1A1A', display: 'flex', alignItems: 'baseline', gap: '2px', whiteSpace: 'nowrap' }}>
              {fundingRateRaw != null ? formatWithSubscript(fundingRateRaw, { suffix: "%" }) : "--"}
              <span style={{ fontFamily: 'General Sans', fontWeight: 500, fontSize: 'clamp(9px, 0.9vw, 11px)', color: '#00000099' }}>/hr</span>
            </span>
          </div>

          {/* Max Open Interest */}
          <div
            className="flex items-center relative"
            style={{ flexShrink: 0, gap: 6 }}
            onMouseEnter={(e) => showTooltip(e, 'The maximum total size of all open positions allowed for this market. Once reached, no new positions can be opened until existing ones are reduced.')}
            onMouseLeave={hideTooltip}
          >
            <span style={{ fontFamily: 'General Sans', fontSize: '11.5px', color: '#98A2B3', cursor: 'help', whiteSpace: 'nowrap' }}>Max OI</span>
            <span style={{ fontFamily: 'General Sans', fontSize: 'clamp(11px, 1vw, 13px)', fontWeight: 700, color: '#1A1A1A', whiteSpace: 'nowrap' }}>
              {formatOIValue(oiData.maxOI)}
            </span>
          </div>

          {/* Asset Information hover button.
              It used to sit in the row above, where it competed with the market
              button and the three price figures for a width none of them could
              give up — the volume figure ended up printing underneath it. Down
              here it is the last item on the secondary row, which has slack. */}
          {marketInfo?.assetInfo && (
            <div className="relative flex-shrink-0" style={{ marginLeft: 'auto' }}>
              <button
                onMouseEnter={() => setAssetInfoOpen(true)}
                onMouseLeave={() => setAssetInfoOpen(false)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'default',
                  fontFamily: '"SF Pro", -apple-system, BlinkMacSystemFont, sans-serif',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  letterSpacing: '0px',
                  color: '#8A8A8A',
                  whiteSpace: 'nowrap',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                  <circle cx="8" cy="8" r="7" stroke="#8A8A8A" strokeWidth="1.5"/>
                  <path d="M8 7v5" stroke="#8A8A8A" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="8" cy="4.5" r="0.75" fill="#8A8A8A"/>
                </svg>
                Asset information
              </button>

              {assetInfoOpen && (
                <div
                  onMouseEnter={() => setAssetInfoOpen(true)}
                  onMouseLeave={() => setAssetInfoOpen(false)}
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    width: '300px',
                    background: '#FFFFFF',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    padding: '16px',
                    zIndex: 200,
                    border: '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <div style={{ fontFamily: 'General Sans', fontSize: '13px', fontWeight: 600, color: '#1A1A1A', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="8" r="7" stroke="#5B1DEE" strokeWidth="1.5"/>
                      <path d="M8 7v5" stroke="#5B1DEE" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="8" cy="4.5" r="0.75" fill="#5B1DEE"/>
                    </svg>
                    {marketInfo.name} — Asset Info
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div>
                      <div style={{ fontFamily: 'General Sans', fontSize: '10px', fontWeight: 600, color: '#5B1DEE', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>What you're trading</div>
                      <div style={{ fontFamily: 'General Sans', fontSize: '12px', color: '#3A3A3A', lineHeight: '1.5' }}>{marketInfo.assetInfo.what}</div>
                    </div>
                    <div style={{ height: '1px', background: 'rgba(0,0,0,0.06)' }} />
                    <div>
                      <div style={{ fontFamily: 'General Sans', fontSize: '10px', fontWeight: 600, color: '#5B1DEE', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Price source</div>
                      <div style={{ fontFamily: 'General Sans', fontSize: '12px', color: '#3A3A3A', lineHeight: '1.5' }}>{marketInfo.assetInfo.priceDriver}</div>
                    </div>
                    <div style={{ height: '1px', background: 'rgba(0,0,0,0.06)' }} />
                    <div>
                      <div style={{ fontFamily: 'General Sans', fontSize: '10px', fontWeight: 600, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Things to know</div>
                      <div style={{ fontFamily: 'General Sans', fontSize: '12px', color: '#3A3A3A', lineHeight: '1.5' }}>{marketInfo.assetInfo.risks}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* === Divider === */}
        {/* A 1px rule, drawn as a 1px box rather than as a border on a
            zero-height div — that combination rendered a 2px line at some zoom
            levels and none at others. */}
        <div style={{ width: '100%', height: '1px', background: 'var(--line-2)' }} />

        {/* === Bottom section: OI, Funding Rate, Max OI === */}

        {/* Mobile layout - bottom */}
        <div className="md:hidden flex items-start justify-between">
          {/* Open Interest */}
          <div
            className="relative"
            onMouseEnter={(e) => showTooltip(e, 'The combined size of all long and short positions in this market.')}
            onMouseLeave={hideTooltip}
          >
            <div style={{ fontFamily: 'General Sans', fontSize: '10px', color: '#667085', cursor: 'help', marginBottom: '2px' }}>Open Interest</div>
            <div className="flex items-center gap-0.5">
              <img src={upArrowIcon} alt="" className="w-[8px] h-[8px]" />
              <span style={{ fontFamily: 'General Sans', fontSize: '10px', fontWeight: 600, color: '#30AA67' }}>{oiData.longPct.toFixed(0)}%</span>
              <span style={{ fontFamily: 'General Sans', fontSize: '9px', color: '#667085' }}>{formatOIValue(oiData.longVal)}</span>
              <img src={downArrowIcon} alt="" className="w-[8px] h-[8px] ml-0.5" />
              <span style={{ fontFamily: 'General Sans', fontSize: '10px', fontWeight: 600, color: '#E4474A' }}>{oiData.shortPct.toFixed(0)}%</span>
              <span style={{ fontFamily: 'General Sans', fontSize: '9px', color: '#667085' }}>{formatOIValue(oiData.shortVal)}</span>
            </div>
          </div>
          {/* Funding Rate */}
          <div
            className="relative text-center"
            onMouseEnter={(e) => {
              const annualRate = fundingRate ? calculateAnnualRate(fundingRate) : '--';
              showTooltip(e, `Annual Rate:\n${annualRate} /year\n\nA time-based fee paid by positions in the direction of the imbalance in long and short open interest. If net open interest is long, these are paid by longs, and if net open interest is short, the fees are paid by shorts. Fees are paid to the protocol.`);
            }}
            onMouseLeave={hideTooltip}
          >
            <div style={{ fontFamily: 'General Sans', fontSize: '10px', color: '#667085', cursor: 'help', marginBottom: '2px' }}>Funding Rate</div>
            <div style={{ fontFamily: 'General Sans', fontSize: '11px', fontWeight: 700, color: '#1A1A1A', display: 'flex', alignItems: 'baseline', gap: '1px', justifyContent: 'center' }}>
              {fundingRateRaw != null ? formatWithSubscript(fundingRateRaw, { suffix: "%" }) : "--"}
              <span style={{ fontFamily: 'General Sans', fontWeight: 500, fontSize: '9px', color: '#00000099' }}>/hr</span>
            </div>
          </div>
          {/* Max Open Interest */}
          <div
            className="relative text-right"
            onMouseEnter={(e) => showTooltip(e, 'The maximum total size of all open positions allowed for this market. Once reached, no new positions can be opened until existing ones are reduced.')}
            onMouseLeave={hideTooltip}
          >
            <div style={{ fontFamily: 'General Sans', fontSize: '10px', color: '#667085', cursor: 'help', marginBottom: '2px' }}>Max Open Interest</div>
            <div style={{ fontFamily: 'General Sans', fontSize: '11px', fontWeight: 700, color: '#1A1A1A' }}>
              {formatOIValue(oiData.maxOI)}
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip.show && (() => {
        const vw = typeof window !== 'undefined' ? window.innerWidth : 400;
        const tooltipW = Math.min(vw - 24, 320);
        const half = tooltipW / 2;
        const pad = 12;
        const clampedX = Math.max(half + pad, Math.min(tooltip.x, vw - half - pad));
        const arrowOffset = tooltip.x - clampedX;
        return (
          <div
            className="fixed z-[9999] pointer-events-none"
            style={{
              left: `${clampedX}px`,
              top: `${tooltip.y}px`,
              transform: 'translate(-50%, -100%)',
              marginTop: '-8px',
              maxWidth: `${tooltipW}px`,
              width: 'max-content',
              padding: '12px 16px',
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              borderRadius: '8px',
              border: '1px solid var(--line)',
              fontSize: '12px',
              fontFamily: 'General Sans',
              fontWeight: 500,
              fontStyle: 'normal',
              lineHeight: '150%',
              letterSpacing: '0%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              whiteSpace: 'pre-line',
            }}
          >
            {tooltip.content}
            <div
              style={{
                position: 'absolute',
                bottom: '-6px',
                left: `calc(50% + ${arrowOffset}px)`,
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                // The tail of the tooltip — a border, not a fill, so the sweep
                // (which only rewrites background-color) cannot reach it.
                borderTop: '6px solid var(--surface)',
              }}
            />
          </div>
        );
      })()}

      {/* Collectibles — full-screen 3D coverflow switcher */}
      {showCollectiblesSwitcher && (
        <MarketSwitcher
          currentId={marketInfo?.id}
          categories={COLLECTIBLES_SWITCHER_CATEGORIES}
          anchorRect={selectorAnchor}
          enterInPlace={browserHandoff}
          prices={allMarketPrices}
          onSelect={(id) => {
            setShowCollectiblesSwitcher(false);
            const target = getMarketById(id);
            if (target) handleMarketSelect(target);
          }}
          onSelectCategory={(key) => {
            // Every non-collectible category hands over to the 3D selector, so
            // the two full-screen switchers stay linked in both directions.
            setShowCollectiblesSwitcher(false);
            setSelectorCategory(key === 'rwa' ? null : key);
            setSelectorAnchor(readMarketButtonRect());
            setBrowserHandoff(true);
            setShowMarketSelector(true);
          }}
          onClose={() => { setShowCollectiblesSwitcher(false); }}
        />
      )}

      {/* Crypto / Stocks / Commodities / Real Estate / Watches — full-screen
          3D selector, opened from "Browse in 3D" in the dropdown header. */}

      {showMarketSelector && (
        <MarketSelector
          currentId={marketInfo?.id}
          initialCategory={selectorCategory}
          anchorRect={selectorAnchor}
          enterInPlace={browserHandoff}
          prices={allMarketPrices}
          getLogo={(m) => (m.iconPath ? getImageUrl(m.iconPath) : (m.coinGeckoId ? getCoinGeckoImageUrl(m.coinGeckoId, m.id) : null))}
          onSelect={(id) => {
            setShowMarketSelector(false);
            const target = getMarketById(id);
            if (target) handleMarketSelect(target);
          }}
          onSelectCollectibles={() => {
            // Pokémon keeps its own Pokédex switcher — the two browsers swap
            // in place, one panel redrawing as the other.
            setShowMarketSelector(false);
            setSelectorAnchor(readMarketButtonRect());
            setBrowserHandoff(true);
            setShowCollectiblesSwitcher(true);
          }}
          onClose={() => { setShowMarketSelector(false); }}
        />
      )}

    </div>
  );
};

/**
 * Category tabs shown inside the Collectibles switcher.
 *
 * Derived from the 3D selector's tab row so the two full-screen switchers
 * always show the same tabs in the same order — only the theming differs.
 * The Pokédex switcher keys Collectibles as `rwa`, matching the dropdown.
 */
const COLLECTIBLES_SWITCHER_CATEGORIES = SELECTOR_CATEGORIES.map((t) =>
  t.key === 'collectibles' ? { key: 'rwa', label: t.label } : t
);

